import * as React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { IPaymentEntryProps } from './PaymentEntry.properties';
import styles from './PaymentEntry.styles';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem, IFormViewModelHandle } from 'components/common/Modals/FormViewModal.properties';
import NumericField from '@markinson/uicomponents-v2/NumericField';
import { default as MUIButton } from '@material-ui/core/Button';
import CheckCircleIcon from '@markinson/uicomponents-v2/SvgIcons/CheckCircle';
import { summaryTableData } from './PaymentEntry.constants';
import { IPaymentLineFacade, IPaymentDetailFacade, IUpdatePaymentLineDetailFacade, IPaymentAmountFacade } from 'api/swaggerTypes';
import classNames from 'classnames';
import DataGrid from 'devextreme-react/data-grid';
import SummaryTable from 'components/common/SummaryTable/SummaryTable';
import { isNull } from 'utils/utils';
import { ObjectifiedPaymentAmountResponse, ObjectifiedPaymentDetail, ObjectifiedPaymentDetailsResponse } from 'api/Worksale/payment';
import { ProcessValidationFormsContext } from 'utils/processValidationForms';
import ChequeDetailsForm from './ChequeDetailsForm';
import CashDetailsForm from './CashDetailsForm';
import GenericDetailsForm from './GenericDetailsForm';
import EftDetailsForm from './EftDetailsForm';
import CreditDetailsForm from './CreditDetailsForm';
import { Inline } from 'api/utils';
import IEftDetailsForm from './IEftDetailsForm';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField';
import { ILookupOption } from 'api/customType';
import { Operation } from 'utils/operations';
import ITextFieldHandle from '@markinson/uicomponents-v2/TextField/TextField.handle';
import DynamicDataGrid from '../DataGridDevEx/DynamicDataGrid';
import { getColumns } from './constants';
import { NativeEventInfo } from 'devextreme/events';
import DevExDataGrid, { KeyDownInfo } from 'devextreme/ui/data_grid';
import { useGridKeyboardNavigation } from '../DataGridDevEx/DataGrid.hooks';

const emptyLookup = { Code: '', Label: '' };
const FOCUS_DELAY = 100;
const FOCUS_AFTER_MESSAGE_DELAY = 300;

function PaymentEntry(props: IPaymentEntryProps): JSX.Element {
  const {
    classes, open, initialValues, isProcessing,
    onApply, onCancel, onAddNewPaymentLine, onCalculatePaymentLineDetails, onUpdatePaymentLineDetails, onDeletePaymentLine, changeConfirmationDialog, onCalculatePaymentAmount } = props;

  const DisableCashLocation = initialValues?.Header?.schema?.CashLocation?.ReadOnly;
  const { onFocusedCellChanging } = useGridKeyboardNavigation();
  const processValidationForms = React.useContext(ProcessValidationFormsContext);

  const [values, setValues] = React.useState<ObjectifiedPaymentDetail>();
  const [context, setContext] = React.useState<IPaymentDetailFacade['Context']>();
  const [header, setHeader] = React.useState<IPaymentDetailFacade['Header']>();
  const [allowOnAccountPayment, setAllowOnAccountPayment] = React.useState<boolean>();
  const [offsetCreditBalance, setOffsetCreditBalance] = React.useState<boolean>();
  const [newLine, setNewLine] = React.useState<IPaymentDetailFacade['NewLine']>();
  const [lines, setLines] = React.useState<IPaymentLineFacade[]>([]);
  const [amount, setAmount] = React.useState(0);
  const [bankingEntity, setBankingEntity] = React.useState<ILookupOption>({ ...emptyLookup });
  const [cashLocation, setCashLocation] = React.useState<ILookupOption>({ ...emptyLookup });
  const [paymentCode, setPaymentCode] = React.useState<ILookupOption>({ ...emptyLookup });
  const [isValidPaymentCode, setIsValidPaymentCode] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState({ amount: false, paymentCode: false });

  const [paymentLineDetail, setPaymentLineDetail] = React.useState<IPaymentLineFacade>();
  const [paymentLineDetailSchema, setPaymentLineDetailSchema] = React.useState<Inline<IPaymentLineFacade>>();
  const [genericDetailsOpen, setGenericDetailsOpen] = React.useState(false);
  const [genericDetailsTitle, setGenericDetailsTitle] = React.useState('');
  const [cashDetailsOpen, setCashDetailsOpen] = React.useState(false);
  const [chequeDetailsOpen, setChequeDetailsOpen] = React.useState(false);
  const [eftDetailsOpen, setEftDetailsOpen] = React.useState(false);
  const [ieftDetailsOpen, setIEftDetailsOpen] = React.useState(false);
  const [creditDetailsOpen, setCreditDetailsOpen] = React.useState(false);
  const [currentSelectedRowKey, setCurrentSelectedRowKey] = React.useState(null);
  const [operationMode, setOperationMode] = React.useState<Operation>(Operation.BROWSE);
  const [isFirstTime, setIsFirstTime] = React.useState<boolean>(true);
  const [isFieldFocused, setIsFieldFocused] = React.useState<boolean>(false);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [isPaymentLineDetailsProcessing, setPaymentLineDetailsProcessing] = React.useState<boolean>(false);

  const prevAmount = React.useRef(amount);
  const isEnterKeyPressed = React.useRef(false);
  const paymentCodeFieldRef = React.useRef<ITextFieldHandle>();
  const amountFieldRef = React.useRef<ITextFieldHandle>();
  const bankingEntityFieldRef = React.useRef<ITextFieldHandle>();
  const formViewModalRef = React.useRef<IFormViewModelHandle>();
  const applyButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const gridRef = React.useRef<DataGrid>();

  const valuesRef = React.useRef(null);
  valuesRef.current = values;

  const selectedRowKeys = React.useMemo(() => currentSelectedRowKey || currentSelectedRowKey === 0 ? [currentSelectedRowKey] : undefined, [currentSelectedRowKey]);

  React.useEffect(
    () => {
      setValues(initialValues);
    },
    [initialValues]
  );

  React.useEffect(
    () => {
      if (isFirstTime && values && paymentCodeFieldRef.current) {
        setTimeout(
          () => {
            paymentCodeFieldRef.current?.select();
            setIsFirstTime(false);
          },
          0
        );
      }
    },
    [isFirstTime, values]
  );

  React.useEffect(
    () => {
      if (values && values.Header) {
        const newHeader = values.Header.inlineObject;
        const newHeaderSchema = values.Header.schema;
        setHeader({
          ...newHeader,
          TenderedAmount: props.module === 'creditnotes' ? 0 : newHeader.TenderedAmount
        });
        if (newHeader) {
          setBankingEntity({
            Label: newHeaderSchema.BankingEntity.Label ? newHeaderSchema.BankingEntity.Label : '',
            Code: newHeaderSchema.BankingEntity.Value ? newHeaderSchema.BankingEntity.Value : ''
          });
          setCashLocation({
            Label: newHeaderSchema.BankingEntity.Label ? newHeaderSchema.BankingEntity.Label : '',
            Code: newHeaderSchema.CashLocation.Value ? newHeaderSchema.CashLocation.Value.toString() : ''
          });
        } else {
          setBankingEntity({ ...emptyLookup });
          setCashLocation({ ...emptyLookup });
        }
      } else {
        setHeader(undefined);
        setBankingEntity({ ...emptyLookup });
        setCashLocation({ ...emptyLookup });
      }
      if (values && values.Context) {
        setContext(values.Context.inlineObject);
      } else {
        setContext(undefined);
      }
      if (values && values.inlineObject) {
        setAllowOnAccountPayment(values.inlineObject.AllowOnAccountPayment);
        setOffsetCreditBalance(values.inlineObject.OffsetCreditBalance);
      } else {
        setAllowOnAccountPayment(false);
        setOffsetCreditBalance(false);
      }
      if (values && values.NewLine) {
        const newNewLine = values.NewLine.inlineObject;
        setNewLine(newNewLine);
        if (newNewLine) {
          setPaymentCode({ Code: newNewLine.PaymentCode });
          setAmount(newNewLine.Amount);
        } else {
          setPaymentCode({ ...emptyLookup });
          setAmount(0);
        }
      } else {
        setNewLine(undefined);
        setPaymentCode({ ...emptyLookup });
        setAmount(0);
      }
      if (values && values.Lines) {
        setLines(values.Lines.map((line) => line.inlineObject));
      } else {
        setLines([]);
      }
    },
    [values]);

  React.useEffect(
    () => {
      if (open) {
        document.addEventListener('keydown', handleSubmitEnter);
        setIsFirstTime(true);
      } else {
        document.removeEventListener('keydown', handleSubmitEnter);
      }
    },
    [open]
  );

  React.useEffect(
    () => {
      if (isFieldFocused && prevAmount.current !== amount && amount !== undefined) {
        setTimeout(
          () => {
            amountFieldRef?.current?.select();
          },
          0
        );
        setErrors({ ...errors, amount: false });
      }
      prevAmount.current = amount;
    },
    [amount, isFieldFocused]
  );

  const handleFocusChange = (isFocused) => {
    setIsFieldFocused(isFocused);
  };

  function confirmationActions(_disableFlag: boolean, isOkDisabled: boolean): IActionItem[] {
    return [
      {
        title: 'Ok',
        isDefault: true,
        disabled: isOkDisabled,
        listener: async () => {
          if (onApply) {
            handleConfirmationBox();
          }
        }
      },
      {
        title: 'Cancel',
        listener: async () => {
          if (onCancel) {
            await onCancel();
            clearPaymentInformation();
          }
        }
      }
    ];
  }

  const COMMAND_COLUMN_WIDTH = 75;
  const isEntitySelected: boolean = !isNull(bankingEntity && bankingEntity.Code);

  const handleSubmitEnter = React.useCallback(
    (event: any): void => {
      const isFocusInsideDialog = event.currentTarget.contains(event.target);
      const isFocusInsideActionButtons = event.target.closest('.MuiDialogActions-root');
      const isFocusInsideApplyButton = event.target.closest('button')?.innerText.includes('APPLY');

      if (isFocusInsideDialog && event.key === 'Enter') {
        if (!isFocusInsideActionButtons && !isFocusInsideApplyButton) {
          event.preventDefault();
        }
        if (valuesRef && valuesRef.current.Header.inlineObject.PaymentTotal >= valuesRef.current.Header.inlineObject.TransactionTotal) {
          if (onApply) {
            handleConfirmationBox();
          }
        }
      }
    },
    [valuesRef]
  );

  const clearPaymentInformation = React.useCallback(
    (): void => {
      setValues(null);
    },
    []);

  async function onUpdatePaymentLineDetailsAndValidate(query: IUpdatePaymentLineDetailFacade): Promise<ObjectifiedPaymentDetailsResponse> {
    const response = await onUpdatePaymentLineDetails(query);

    return processValidationForms(response, query, onUpdatePaymentLineDetailsAndValidate);
  }

  function detailsDialogBoxOpen(dialogBoxName: string, genericDescription: string): void {
    switch (dialogBoxName) {
      case 'GenericPaymentDetails':
        setGenericDetailsTitle(`${genericDescription} Details`);
        setGenericDetailsOpen(true);
        break;
      case 'CashPaymentDetails':
        setCashDetailsOpen(true);
        break;
      case 'ChequePaymentDetails':
        setChequeDetailsOpen(true);
        break;
      case 'ElectronicPaymentDetails':
        setEftDetailsOpen(true);
        break;
      case 'IntegratedElectronicPaymentDetails':
        setIEftDetailsOpen(true);
        break;
      case 'CreditCardPaymentDetails':
        setCreditDetailsOpen(true);
        break;
      default:
        return;
    }
  }

  async function showDetailsAndUpdate(formId: string, paymentLine: IPaymentLineFacade, schema: Inline<IPaymentLineFacade>): Promise<void> {
    setPaymentLineDetailSchema(schema);
    setPaymentLineDetail(paymentLine);
    setOperationMode(Operation.EDIT);
    detailsDialogBoxOpen(formId, paymentLine.PaymentDescription);
  }

  function handleCancelDetails(): void {
    setGenericDetailsOpen(false);
    setCashDetailsOpen(false);
    setChequeDetailsOpen(false);
    setEftDetailsOpen(false);
    setIEftDetailsOpen(false);
    setCreditDetailsOpen(false);
    setPaymentLineDetailSchema(undefined);
    setPaymentLineDetail(undefined);
    setOperationMode(Operation.BROWSE);

    focusPaymentCodeField();
  }

  async function handleApplyDetails(line: IPaymentLineFacade): Promise<void> {
    setPaymentLineDetailsProcessing(true);
    const updateLineResponse = await onUpdatePaymentLineDetailsAndValidate({
      PaymentDetail: {
        ...values.inlineObject,
        Context: values.Context.inlineObject,
        Header: {
          ...header,
          BankingEntity: bankingEntity.Code,
          CashLocation: parseInt(cashLocation.Code),
        },
        NewLine: values.NewLine.inlineObject,
        Lines: values.Lines.map((l) => l.inlineObject)
      },
      UpdateLine: line,
    });

    setPaymentLineDetailsProcessing(false);

    if (updateLineResponse.Status && updateLineResponse.PaymentDetail) {
      setValues(updateLineResponse.PaymentDetail);
      setGenericDetailsOpen(false);
      setCashDetailsOpen(false);
      setChequeDetailsOpen(false);
      setEftDetailsOpen(false);
      setIEftDetailsOpen(false);
      setCreditDetailsOpen(false);
      setPaymentLineDetailSchema(undefined);
      setPaymentLineDetail(undefined);
      setOperationMode(Operation.BROWSE);

      if (updateLineResponse?.PaymentDetail?.Header?.inlineObject?.OnAccount > 0) {
        focusPaymentCodeField();
      } else {
        focusOkButton();
      }
    }
  }

  async function onAddNewPaymentLineAndValidate(query: IPaymentDetailFacade): Promise<void> {
    let response = await onAddNewPaymentLine(query);
    const screenRequest = response.Forms ? response.Forms.find((form) => form.FormType === 'ScreenRequest') : undefined;
    const messageRequest = response.Forms ? response.Forms.find((form) => form.FormType === 'Message') : undefined;
    if (!screenRequest || messageRequest) {
      response = await processValidationForms(response, query, onAddNewPaymentLineAndValidate);

      if (response.Status && response.PaymentDetail) {
        setValues(response.PaymentDetail);
        setTimeout(
          () => {
            if (response?.PaymentDetail?.Header?.inlineObject?.OnAccount === 0) {
              focusOkButton();
            } else {
              focusPaymentCodeField();
            }
          },
          FOCUS_AFTER_MESSAGE_DELAY
        );
      }
    }

    if (screenRequest) {
      await showDetailsAndUpdate(
        screenRequest.FormId,
        response.PaymentDetail.NewLine.inlineObject,
        response.PaymentDetail.NewLine.schema);
    }
  }

  async function onDeletePaymentLineAndValidate(query: IPaymentDetailFacade, PositionNumber: number): Promise<ObjectifiedPaymentDetailsResponse> {
    return processValidationForms(await onDeletePaymentLine(query, PositionNumber), query, async (newQuery) => onDeletePaymentLineAndValidate(newQuery, PositionNumber));
  }

  function blurPaymentEntryFields(): void {
    amountFieldRef.current.blur();
    paymentCodeFieldRef.current.blur();
  }

  async function handleAddLine(newAmount: number | null = null): Promise<void> {
    if (isDirty) {
      isEnterKeyPressed.current = true;
      if (!isValidPaymentCode) {
        focusPaymentCodeField();
      } else if (amount === undefined) {
        setTimeout(
          () => {
            amountFieldRef.current?.focus();
          },
          0
        );
      }

      return;
    }
    isEnterKeyPressed.current = false;

    if (amount && paymentCode?.Code && isValidPaymentCode) {
      setOperationMode(Operation.NEW);
      if (onAddNewPaymentLine && onUpdatePaymentLineDetails) {
        await onAddNewPaymentLineAndValidate({
          Context: context,
          Header: {
            ...header,
            BankingEntity: bankingEntity.Code,
            CashLocation: parseInt(cashLocation.Code),
          },
          AllowOnAccountPayment: allowOnAccountPayment,
          OffsetCreditBalance: offsetCreditBalance,
          Lines: lines,
          NewLine: {
            ...newLine,
            PaymentCode: paymentCode.Code,
            PaymentDescription: paymentCode.Label,
            Amount: newAmount ?? amount
          }
        });
      }
    } else {
      if (!isValidPaymentCode) {
        focusPaymentCodeField();
      } else if (amount === undefined) {
        setTimeout(
          () => {
            amountFieldRef.current?.focus();
          },
          0
        );
      }
    }
  }

  async function handleUpdateLine(data: IPaymentLineFacade): Promise<void> {
    const schema = values.Lines.find((line) => line.inlineObject.PositionNumber === data.PositionNumber).schema;
    setPaymentLineDetailSchema(schema);
    setPaymentLineDetail(data);

    if (data.PaymentCode === 'IEFT') {
      setOperationMode(Operation.BROWSE);
      setIEftDetailsOpen(true);
    } else {
      setOperationMode(Operation.EDIT);
      detailsDialogBoxOpen(data.ScreenCode, data.PaymentDescription);
    }
  }

  async function handleRemoveLine(data: IPaymentLineFacade): Promise<void> {
    if (onDeletePaymentLine) {
      const response = await onDeletePaymentLineAndValidate(
        {
          Context: context,
          Header: {
            ...header,
            BankingEntity: bankingEntity.Code,
            CashLocation: parseInt(cashLocation.Code),
          },
          AllowOnAccountPayment: allowOnAccountPayment,
          OffsetCreditBalance: offsetCreditBalance,
          Lines: lines,
        },
        data.PositionNumber);

      if (response.Status && response.PaymentDetail) {
        setValues(response.PaymentDetail);
        if (response?.PaymentDetail?.Header?.inlineObject?.OnAccount > 0) {
          focusPaymentCodeField();
        }
      }
    }
  }

  const gridOnKeyDown =
    async (e: NativeEventInfo<DevExDataGrid<any, any>, KeyboardEvent> & KeyDownInfo) => {
      const keyboardEvent = e.event as KeyboardEvent;
      const selKey = e.component.getSelectedRowKeys();

      if (keyboardEvent.key === 'ArrowUp' || keyboardEvent.key === 'ArrowDown') {
        keyboardEvent.stopPropagation();
        const visibleRowsLength = await e.component.getVisibleRows().length;

        if (selKey.length) {
          const currentKey = selKey[0];
          let index = e.component.getRowIndexByKey(currentKey);
          if (keyboardEvent.key === 'ArrowUp') {
            index--;
            if (index >= 0) {
              await e.component.selectRowsByIndexes([index]);
            }
          } else if (keyboardEvent.key === 'ArrowDown') {
            index++;
            if (index < visibleRowsLength) {
              await e.component.selectRowsByIndexes([index]);
            }
          }
        }
        await e.component.focus();
      }

      if (keyboardEvent.key === 'Tab') {
        if (keyboardEvent.shiftKey) {
          setTimeout(
            () => {
              amountFieldRef.current.focus();
              e.component.clearSelection();
            },
            0
          );
        } else {
          setTimeout(
            () => {
              formViewModalRef.current?.focusFirstButton();
              e.component.clearSelection();
            },
            0
          );
        }
      }

      if (keyboardEvent.key === 'Enter' && keyboardEvent.ctrlKey && selKey.length) {
        keyboardEvent.stopPropagation();
        handleUpdateLine(selKey[0]);
        keyboardEvent.preventDefault();
      }

      if (keyboardEvent.key === 'Delete' && selKey.length) {
        handleRemoveLine(selKey[0]);
        keyboardEvent.preventDefault();
      }
    };

  const selectFirstRow = () => {
    gridRef.current?.instance.selectRowsByIndexes([0])
      .catch((err) => console.warn(err));
  };

  const gridOnSelectionChanged = React.useCallback(
    (e) => {
      const selectedRowKey = e.selectedRowKeys && e.selectedRowKeys.length > 0 ? e.selectedRowKeys[0] as unknown : undefined;
      setCurrentSelectedRowKey(selectedRowKey);
    },
    []
  );

  const handleApplyButtonKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      if (!event.shiftKey) {
        setTimeout(
          () => {
            if (lines?.length) {
              gridRef.current?.instance.focus();
              setTimeout(
                () => {
                  selectFirstRow();
                },
                0
              );
            } else {
              formViewModalRef?.current?.focusFirstButton();
            }
          },
          0
        );
      }
    }
  };

  const focusOkButton = () => {
    setTimeout(
      () => {
        formViewModalRef.current?.focusLastButton();
      },
      FOCUS_DELAY
    );
  };

  async function onCalculatePaymentAmountAndValidate(query: IPaymentAmountFacade): Promise<ObjectifiedPaymentAmountResponse> {
    return processValidationForms(await onCalculatePaymentAmount(query), query, async (newQuery) => onCalculatePaymentAmountAndValidate(newQuery));
  }

  async function setPaymentAmount(item: any): Promise<void> {
    if (item) {
      const response = await onCalculatePaymentAmountAndValidate({ PaymentCode: item.Code, Amount: values?.Header?.inlineObject?.OnAccount || amount });
      if (response.Status && response.PaymentAmount) {
        setPaymentCode(item);
        setIsValidPaymentCode(true);
        setAmount(response.PaymentAmount.inlineObject.Amount);
        setErrors({ ...errors, amount: false, paymentCode: false });
        setIsDirty(false);
      }
    } else {
      setIsValidPaymentCode(false);
      isEnterKeyPressed.current = false;
    }
  }

  React.useEffect(
    () => {
      if (!isDirty && isEnterKeyPressed.current) {
        handleAddLine(null);
      }
    },
    [isDirty]
  );

  async function handleConfirmationBox(): Promise<void> {
    if (!bankingEntity?.Code || !cashLocation?.Code) {// as these are required fields
      return;
    }

    if (changeConfirmationDialog) {
      changeConfirmationDialog({
        open: true,
        title: 'Question',
        message: 'Confirm Payment.\nAre you sure?',
        okLabel: 'Yes',
        showRefuse: true,
        showCancel: false,
        defaultBtnNo: '3',
        onRefuse: () => {
          focusOkButton();
        },
        onOk: () => {
          onApply({
            Context: valuesRef.current.Context.inlineObject,
            Header: valuesRef.current.Header.inlineObject,
            AllowOnAccountPayment: valuesRef.current.inlineObject.AllowOnAccountPayment,
            OffsetCreditBalance: valuesRef.current.inlineObject.OffsetCreditBalance,
            Lines: valuesRef.current.Lines.map((line) => line.inlineObject)
          });
        }
      });
    }
  }

  function getModalContent(): JSX.Element {
    const onBankingEntitySelectionChanged = React.useCallback(
      (item) => {
        if (initialValues?.Header?.inlineObject?.BankingEntity !== item?.Code ||
          initialValues?.Header?.inlineObject?.CashLocation !== Number(cashLocation?.Code)) {
          setBankingEntity(item as any);
          setCashLocation(null);
        }
      },
      [initialValues, cashLocation]
    );

    return <div className={classes.mainDiv}>
      <div className={classes.formDiv}>
        <BasicLookupActionField
          required
          placeholder={''}
          label={'Banking entity'}
          size={'small'}
          lookupName={'Entity'}
          disabled={lines.length > 0}
          value={bankingEntity ? bankingEntity.Code : ''}
          onSelectedItemChange={onBankingEntitySelectionChanged}
          ref={bankingEntityFieldRef}
        />
        <BasicLookupActionField
          required
          placeholder={''}
          label={'Cash Location'}
          size={'small'}
          lookupName={'CashLocation'}
          isEntityScoped={true}
          value={cashLocation ? cashLocation.Code : ''}
          disabled={!isEntitySelected || DisableCashLocation || lines.length > 0}
          params={isEntitySelected ? { Entity: bankingEntity.Code } : null}
          onSelectedItemChange={(item) => setCashLocation(item as any)}
        />
        <div className={classes.paymentLineDiv}>
          <BasicLookupActionField
            className={classes.paymentLookupField}
            placeholder={''}
            label={'Payment'}
            size={'small'}
            lookupName={'PaymentCode'}
            suppressDescription={true}
            onSelectedItemChange={setPaymentAmount}
            value={paymentCode ? paymentCode.Code : ''}
            ref={paymentCodeFieldRef}
            onChange={(v) => {
              if (v) {
                setErrors({ ...errors, paymentCode: false });
              } else {
                setErrors({ ...errors, paymentCode: true });
              }
            }}
            onEnterKeyDown={handlePaymentCodeKeydown}
            onValueChange={() => {
              setIsDirty(true);
            }}
            required={errors?.paymentCode}
          />
          <NumericField
            className={classes.amountField}
            label={'Amount'}
            size={'medium'}
            value={amount}
            onBlur={(v) => {
              setAmount(v);
              handleFocusChange(false);
            }}
            onChange={(v) => {
              if (v !== undefined) {
                setErrors({ ...errors, amount: false });
              } else {
                setErrors({ ...errors, amount: true });
              }
            }}
            onKeyDown={handleAmountKeyDown}
            ref={amountFieldRef}
            onFocus={() => handleFocusChange(true)}
            required={errors?.amount}
          />
          <MUIButton
            onClick={() => handleAddLine(null)}
            onKeyDown={handleApplyButtonKeyDown}
            variant={'contained'}
            className={classNames(classes.button)}
            buttonRef={(Ref) => applyButtonRef.current = Ref}
          >
            <CheckCircleIcon style={{ color: 'green' }} />
            APPLY
          </MUIButton>
        </div>
        <DynamicDataGrid
          key={'LineNumber'}
          dataGridClassName={classes.paymentLineDataGrid}
          onDelete={(_, data) => handleRemoveLine(data)}
          onEditClick={(_, data) => handleUpdateLine(data)}
          columns={getColumns()}
          allowUpdating={true}
          allowHeaderFilting={false}
          standardKeyboardNavigation={false}
          actionsColWidth={COMMAND_COLUMN_WIDTH}
          gridProps={{
            dataGridRef: gridRef,
            dataSource: lines,
            hoverStateEnabled: true,
            onKeyDown: gridOnKeyDown,
            onSelectionChanged: gridOnSelectionChanged,
            onFocusedCellChanging: onFocusedCellChanging,
            selection: { mode: 'single' },
            selectedRowKeys: selectedRowKeys
          }}
        />
      </div>
      <div className={classes.tableDiv}>

        <SummaryTable
          data={summaryTableData}
          Header={header}
        />
      </div>
    </div>;
  }

  function handleAmountKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      const newAmount = parseFloat((event.target as HTMLInputElement).value);
      blurPaymentEntryFields();
      if ((event.target as HTMLInputElement).value) {
        handleAddLine(!isNaN(newAmount) ? newAmount : null);
      }
    } else if (event.key === 'Tab') {
      if (event.shiftKey) {
        focusPaymentCodeField();
      } else {
        setTimeout(
          () => {
            applyButtonRef.current.focus();
          },
          FOCUS_DELAY
        );
      }
    }
  }

  const handlePaymentCodeKeydown = () => {
    if (!errors?.paymentCode) {
      handleAddLine(null);
    }
  };

  const focusGrid = React.useCallback(
    () => {
      setTimeout(
        () => {
          if (lines?.length) {
            gridRef.current?.instance.focus();
            setTimeout(
              () => {
                selectFirstRow();
              },
              0
            );
          } else {
            if (applyButtonRef?.current) {
              setTimeout(
                () => {
                  applyButtonRef.current?.focus();
                },
                0
              );
            }
          }
        },
        0
      );
    },
    [gridRef.current, paymentCodeFieldRef.current]
  );

  const focusFirstField = React.useCallback(
    () => {
      if (lines.length === 0) {
        setTimeout(
          () => {
            bankingEntityFieldRef.current.focus();
          },
          0
        );
      } else {
        focusPaymentCodeField();
      }
    },
    [paymentCodeFieldRef.current, gridRef.current, lines, header, values]
  );

  const focusPaymentCodeField = React.useCallback(
    () => {
      setTimeout(
        () => {
          paymentCodeFieldRef.current?.focus();
          gridRef?.current?.instance?.clearSelection();
        },
        FOCUS_DELAY
      );
    },
    [paymentCodeFieldRef.current, gridRef.current]
  );

  return (
    <React.Fragment>
      <FormViewModal
        open={open}
        innerRef={formViewModalRef}
        loading={values === null || values === undefined}
        title={'Payment Entry'}
        modalContent={getModalContent()}
        actions={confirmationActions(!(header?.OnAccount === 0), isProcessing)}
        dialogActionsButtons={true}
        dialogActionsShadow={false}
        onActionShiftTabOut={focusGrid}
        onActionsTabOut={focusFirstField}
      />
      {genericDetailsOpen ?
        <GenericDetailsForm
          open={genericDetailsOpen}
          title={genericDetailsTitle}
          initialValues={paymentLineDetail}
          schema={paymentLineDetailSchema}
          isProcessing={isPaymentLineDetailsProcessing}
          onApply={handleApplyDetails}
          onCancel={handleCancelDetails} /> : undefined}
      {cashDetailsOpen ?
        <CashDetailsForm
          module={props?.module}
          open={cashDetailsOpen}
          initialValues={paymentLineDetail}
          schema={paymentLineDetailSchema}
          isProcessing={isPaymentLineDetailsProcessing}
          onApply={handleApplyDetails}
          onCancel={handleCancelDetails} /> : undefined}
      {chequeDetailsOpen ?
        <ChequeDetailsForm
          open={chequeDetailsOpen}
          initialValues={paymentLineDetail}
          schema={paymentLineDetailSchema}
          isProcessing={isPaymentLineDetailsProcessing}
          onApply={handleApplyDetails}
          onCancel={handleCancelDetails} /> : undefined}
      {eftDetailsOpen ?
        <EftDetailsForm
          open={eftDetailsOpen}
          paymentDetail={{
            ...values.inlineObject,
            Context: values.Context.inlineObject,
            Header: values.Header.inlineObject,
            NewLine: values.NewLine.inlineObject,
            Lines: values.Lines.map((l) => l.inlineObject)
          }}
          initialValues={paymentLineDetail}
          schema={paymentLineDetailSchema}
          isProcessing={isPaymentLineDetailsProcessing}
          onCalculatePaymentLineDetails={onCalculatePaymentLineDetails}
          onApply={handleApplyDetails}
          onCancel={handleCancelDetails} /> : undefined}
      {ieftDetailsOpen ?
        <IEftDetailsForm
          open={ieftDetailsOpen}
          operationMode={operationMode}
          paymentDetail={{
            ...values.inlineObject,
            Context: values.Context.inlineObject,
            Header: values.Header.inlineObject,
            NewLine: values.NewLine.inlineObject,
            Lines: values.Lines.map((l) => l.inlineObject)
          }}
          initialValues={paymentLineDetail}
          schema={paymentLineDetailSchema}
          isProcessing={isPaymentLineDetailsProcessing}
          onCalculatePaymentLineDetails={onCalculatePaymentLineDetails}
          onApply={handleApplyDetails}
          onCancel={handleCancelDetails} /> : undefined}
      {creditDetailsOpen ?
        <CreditDetailsForm
          open={creditDetailsOpen}
          paymentDetail={{
            ...values.inlineObject,
            Context: values.Context.inlineObject,
            Header: values.Header.inlineObject,
            NewLine: values.NewLine.inlineObject,
            Lines: values.Lines.map((l) => l.inlineObject)
          }}
          initialValues={paymentLineDetail}
          schema={paymentLineDetailSchema}
          isProcessing={isPaymentLineDetailsProcessing}
          onCalculatePaymentLineDetails={onCalculatePaymentLineDetails}
          onApply={handleApplyDetails}
          onCancel={handleCancelDetails} /> : undefined}
    </React.Fragment>);
}

export default withStyles(styles, { index: 1 })(PaymentEntry);
